import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaFolderOpen,
  FaListOl,
  FaPlus,
  FaSave,
  FaTimes,
} from "react-icons/fa";

import {
  DOSSIER_QUILL_FORMATS,
  DOSSIER_QUILL_MODULES,
} from "../utils/dossierInscriptionUtils";

export default function SegmentCreateDrawer({
  isOpen,
  submitting,
  newSegment,
  setNewSegment,
  newFormKey,
  onClose,
  onSubmit,
  onReset,
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Nouveau segment
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaFolderOpen className="text-indigo-600 dark:text-indigo-300" />
              Créer une section du dossier
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Créez un segment pour organiser les informations du dossier
              d’inscription. Vous pourrez ensuite lui ajouter des fichiers.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

            <div className="p-4 sm:p-5 lg:p-6">
              <div className="grid gap-4 md:grid-cols-[1fr,180px]">
                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Nom du segment
                  </label>

                  <input
                    type="text"
                    value={newSegment.segment_name || ""}
                    onChange={(event) =>
                      setNewSegment((prev) => ({
                        ...prev,
                        segment_name: event.target.value,
                      }))
                    }
                    placeholder="Ex : Documents à fournir"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Ordre
                  </label>

                  <div className="relative">
                    <FaListOl className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                    <input
                      type="number"
                      value={newSegment.ordre_segment ?? 0}
                      onChange={(event) =>
                        setNewSegment((prev) => ({
                          ...prev,
                          ordre_segment: Number(event.target.value) || 0,
                        }))
                      }
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Contenu du segment
                </label>

                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 quill-wrapper">
                  <ReactQuill
                    key={newFormKey}
                    theme="snow"
                    value={newSegment.segment_content || ""}
                    onChange={(value) =>
                      setNewSegment((prev) => ({
                        ...prev,
                        segment_content: value,
                      }))
                    }
                    placeholder="Contenu du segment"
                    modules={DOSSIER_QUILL_MODULES}
                    formats={DOSSIER_QUILL_FORMATS}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onReset}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Réinitialiser
            </button>

            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              {submitting ? <FaSave /> : <FaPlus />}
              {submitting ? "Création..." : "Créer le segment"}
            </button>
          </div>
        </div>

        <style>{`
          .quill-wrapper .ql-toolbar.ql-snow {
            border: 0;
            border-bottom: 1px solid #e2e8f0;
            background: #ffffff;
          }

          .quill-wrapper .ql-container.ql-snow {
            border: 0;
            min-height: 260px;
          }

          .quill-wrapper .ql-editor {
            min-height: 260px;
            font-size: 14px;
            line-height: 1.7;
          }

          .dark .quill-wrapper .ql-toolbar.ql-snow {
            background: rgba(15,23,42,.98);
            border-color: #334155;
          }

          .dark .quill-wrapper .ql-container.ql-snow {
            background: rgba(2,6,23,.7);
          }

          .dark .quill-wrapper .ql-editor {
            color: #e2e8f0;
          }

          .dark .ql-snow .ql-stroke {
            stroke: #e2e8f0 !important;
          }

          .dark .ql-snow .ql-fill {
            fill: #e2e8f0 !important;
          }

          .dark .ql-snow .ql-picker,
          .dark .ql-snow .ql-picker-label,
          .dark .ql-snow .ql-picker-options,
          .dark .ql-snow .ql-toolbar button {
            color: #e2e8f0 !important;
          }

          .dark .ql-snow .ql-picker-options {
            background: #0b1220;
            border-color: #334155;
          }
        `}</style>
      </aside>
    </div>
  );
}